/**
 * Builds a pre-filled `/dash/support` URL that opens the in-app
 * Contact Support modal with every piece of context the engineer
 * needs to reproduce a client-side crash: the error digest (the
 * Next.js correlation ID), the propertyId / portfolioId, the section name,
 * the source URL, and a short error summary. The user can append
 * additional notes in the modal's Question field before submitting.
 *
 * Why this exists: when the user hits an error fallback (any of
 * `global-error.tsx`, `dash/error.tsx`, `(with-chat)/error.tsx`, a
 * per-route `error.tsx`, or a `<SafeBoundary>` default fallback) the
 * "Try again" button is often useless because the upstream data is
 * still in the same broken state. The escape hatch is to reach a
 * human — but if the user has to copy/paste IDs manually, most
 * never do. This URL builder removes that friction.
 *
 * Topic is hard-coded to `'Bug / Performance'` which is the canonical
 * support-topic for crashes (matches `services/support/schema.ts`).
 *
 * The `Question` template is intentionally separated by a `--- Your
 * notes below ---` marker so the user sees a clear place to add
 * context and the support engineer sees a clear boundary between
 * machine-generated diagnostics and user-supplied notes.
 */

export interface SupportErrorContext {
  /** Next.js error digest (when available). */
  errorId?: string | null;
  /** Short human-readable error summary — typically `error.message`. */
  errorMessage?: string | null;
  /** Property UUID, if the error happened on a property page. */
  propertyId?: string | null;
  /** Portfolio UUID, if known. */
  portfolioId?: string | null;
  /**
   * Human-readable section / boundary name (e.g. "Tax History",
   * "Chat Panel", "Property Workspace"). Helps support quickly
   * identify which subsystem failed.
   */
  sectionName?: string | null;
  /**
   * Full URL the user was on when the error fired. Defaults to
   * `window.location.href` when called client-side.
   */
  sourceUrl?: string | null;
}

const DEFAULT_TOPIC = 'Bug / Performance';

/**
 * Resolves the absolute support URL. We deliberately target the
 * PUBLIC `/support` route (NOT `/dash/support`) so the escape hatch
 * works even when the dash layout itself is crashing — `/dash/**`
 * loads `DashLayout` which calls `clerkClient().users.getUser()`
 * and `getSubscriptionState()`. If Clerk's API is down (500), the
 * entire dash subtree blanks and `/dash/support` becomes
 * unreachable. The public `/support` page mounts directly under
 * the root layout, has no auth dependencies, and renders even
 * during the worst failure modes.
 *
 * The URL is always absolute (origin + path) so it resolves
 * regardless of the user's current location and works as a fallback
 * even if the local Next.js process is degraded.
 */
function resolveSupportOrigin(): string {
  // Prefer the deployment's configured site URL (production: relm.ai;
  // staging: staging.relm.ai). Fall back to production relm.ai as the
  // always-up failsafe — production is monitored 24/7 so the support
  // page is the most reliable URL we can hand a broken client.
  const fromEnv =
    process.env.NEXT_PUBLIC_SITE_URL ||
    (process.env.VERCEL_PROJECT_PRODUCTION_URL ?
      `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`
    : null);
  return fromEnv ?? 'https://relm.ai';
}

/**
 * Build the `?question=...` body. Engineers should NEVER edit this
 * format without updating the SectionFallback / error.tsx callers
 * too — the support team's triage workflow grep's for the
 * `Error ID:` / `Property ID:` / `Portfolio ID:` line prefixes.
 */
function buildQuestionBody(ctx: SupportErrorContext): string {
  const lines: string[] = ['I hit an error in Relm Pro and need help.', ''];

  if (ctx.errorId) lines.push(`Error ID: ${ctx.errorId}`);
  if (ctx.errorMessage) lines.push(`Error: ${ctx.errorMessage}`);
  if (ctx.sectionName) lines.push(`Section: ${ctx.sectionName}`);
  if (ctx.propertyId) lines.push(`Property ID: ${ctx.propertyId}`);
  if (ctx.portfolioId) lines.push(`Portfolio ID: ${ctx.portfolioId}`);
  if (ctx.sourceUrl) lines.push(`URL: ${ctx.sourceUrl}`);

  lines.push('');
  lines.push('--- Your notes below ---');
  lines.push('');
  lines.push(
    '(What were you doing when this happened? Any extra context helps.)',
  );
  return lines.join('\n');
}

export function buildSupportErrorUrl(ctx: SupportErrorContext): string {
  const params = new URLSearchParams();
  params.set('topic', DEFAULT_TOPIC);
  params.set('question', buildQuestionBody(ctx));
  return `${resolveSupportOrigin()}/support?${params.toString()}`;
}

/**
 * Resolves the source URL safely (no `window` on the server) — for
 * use inside Client Component render paths.
 */
export function safeSourceUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.location.href;
  } catch {
    return null;
  }
}
